
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Search,
    Mail,
    Phone,
    Calendar,
    User,
    Home,
    ArrowLeft,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { getAllAdminInquiries } from '@/services/inquiry';
import { toast } from '@/hooks/use-toast';
import { PaginationControl } from '@/components/shared/PaginationControl';
import { pagination } from '@/constants';
import { capitalizeWords } from '@/helper';
import { MessageDialog } from '@/components/shared/MessageDialog';

interface Inquiry {
    _id: string;
    contactInfo: {
        name: string;
        email: string;
        phone: string;
    };
    user?: {
        name: string;
    };
    property: {
        specifications: {
            titleDeed: string;
        };
    };
    message: string;
    createdAt: string;
    inquiryType?: 'property' | 'general';
}

const Leads = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [inquiryTypeFilter, setInquiryTypeFilter] = useState('all');
    const [leads, setLeads] = useState<Inquiry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);

    // Debounce search query
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchQuery]);

    const fetchLeads = async (page = pagination.page) => {
        setIsLoading(true);
        try {
            const { data, pagination: paginationData } = await getAllAdminInquiries({
                page: page,
                limit: pagination.limit,
                searchQuery: debouncedSearchQuery,
                inquiryType: inquiryTypeFilter !== 'all' ? inquiryTypeFilter : undefined
            });

            if (Array.isArray(data)) {
                setLeads(data);
                if (paginationData) {
                    setTotalPages(paginationData.totalPages);
                }
            } else {
                console.error("Unexpected API response format:", data);
            }
        } catch (error) {
            console.error('Error fetching leads:', error);
            toast({
                title: "Error",
                description: "Failed to load leads",
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    };

    React.useEffect(() => {
        fetchLeads();
    }, [debouncedSearchQuery, inquiryTypeFilter]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };



    return (
        <div className="min-h-screen flex flex-col">
            <Header />

            <div className="pt-32 pb-20 bg-secondary/30 flex-1">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-bold">Leads Management</h1>
                            <p className="text-muted-foreground">Track and manage property inquiries</p>
                        </div>
                        <Button variant="outline" asChild>
                            <Link to="/admin/dashboard">
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back to Dashboard
                            </Link>
                        </Button>
                    </div>

                    <Card className="mb-8">
                        <CardHeader className="pb-3">
                            <CardTitle>Filters</CardTitle>
                            <CardDescription>Search and filter leads</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="relative flex-1">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="text"
                                        placeholder="Search by name, email, or mobile..."
                                        className="pl-8"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                                <Select value={inquiryTypeFilter} onValueChange={setInquiryTypeFilter}>
                                    <SelectTrigger className="w-full md:w-[200px]">
                                        <SelectValue placeholder="Filter by Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All</SelectItem>
                                        <SelectItem value="property">Property Inquiry</SelectItem>
                                        <SelectItem value="general">General Inquiry</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="rounded-md border bg-card">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Lead Name</TableHead>
                                    <TableHead>Contact Info</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Property Interest</TableHead>
                                    <TableHead>Message</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Assigned Broker</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map((i) => (
                                        <TableRow key={i}>
                                            <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                            <TableCell>
                                                <div className="flex flex-col gap-1">
                                                    <Skeleton className="h-3 w-40" />
                                                    <Skeleton className="h-3 w-32" />
                                                </div>
                                            </TableCell>
                                            <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Skeleton className="h-3 w-3 rounded-full" />
                                                    <Skeleton className="h-3 w-32" />
                                                </div>
                                            </TableCell>
                                            <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Skeleton className="h-3 w-3 rounded-full" />
                                                    <Skeleton className="h-3 w-24" />
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Skeleton className="h-3 w-3 rounded-full" />
                                                    <Skeleton className="h-3 w-24" />
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : leads.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                            No leads found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    leads.map((lead) => (
                                        <TableRow key={lead._id}>
                                            <TableCell>
                                                <div className="font-medium">{capitalizeWords(lead.contactInfo?.name || lead.user?.name || 'Guest')}</div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex items-center text-sm">
                                                        <Mail className="h-3 w-3 mr-2 text-muted-foreground" />
                                                        {lead.contactInfo?.email || 'N/A'}
                                                    </div>
                                                    <div className="flex items-center text-sm">
                                                        <Phone className="h-3 w-3 mr-2 text-muted-foreground" />
                                                        {lead.contactInfo?.phone || 'N/A'}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={lead.inquiryType === 'general' ? 'secondary' : 'outline'}>
                                                    {lead.inquiryType === 'general' ? 'General' : 'Property'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center max-w-[200px]">
                                                    {lead.inquiryType === 'property' ? (
                                                        <>
                                                            <Home className="h-3 w-3 mr-2 text-muted-foreground flex-shrink-0" />
                                                            <span className="truncate" title={lead.property?.specifications?.titleDeed}>
                                                                {capitalizeWords(lead.property?.specifications?.titleDeed || 'N/A')}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className="text-muted-foreground text-sm italic">General Inquiry</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="max-w-xs">
                                                <div className="flex items-center">
                                                    <span className="truncate block max-w-[150px]" title={lead.message}>
                                                        {lead.message}
                                                    </span>
                                                    <MessageDialog message={lead.message} />
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center text-sm">
                                                    <Calendar className="h-3 w-3 mr-2 text-muted-foreground" />
                                                    {formatDate(lead.createdAt)}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center text-sm">
                                                    <User className="h-3 w-3 mr-2 text-muted-foreground" />
                                                    {capitalizeWords(lead.user?.name || 'N/A')}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-center mt-4">
                        <PaginationControl
                            key={`${debouncedSearchQuery}-${inquiryTypeFilter}`} // Reset on filter change
                            totalPages={totalPages}
                            onPageChange={fetchLeads}
                        />
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Leads;
